import { Link } from '@inertiajs/react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import type { HackerDashboardProject } from '@/types';

const STATUS_CONFIG = {
  submitted: {
    label: 'ENTREGA VÁLIDA',
    pillClass: 'bg-primary text-background',
  },
  requirement_missing: {
    label: 'FALTAN REQUISITOS',
    pillClass: 'bg-destructive text-destructive-foreground',
  },
  unsubmitted: {
    label: 'AÚN NO ENTREGAS',
    pillClass: 'bg-muted text-muted-foreground',
  },
} as const;

function parseErrors(submissionError: string | null): string[] {
  if (!submissionError) return [];
  return submissionError
    .split('\n')
    .map((line) => line.replace(/^❌\s*/, '').trim())
    .filter(Boolean);
}

export function ProjectSubmissionCard({
  project,
  titleFont,
  submissionHint,
}: {
  project: HackerDashboardProject;
  titleFont: string | null;
  submissionHint: string;
}) {
  const status = project.submissionStatus;
  const config = STATUS_CONFIG[status];

  return (
    <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
      <div className="space-y-6">
        <div className="border-primary/20 border-b pb-4">
          <h2
            className={cn(
              'font-bold text-2xl text-primary sm:text-3xl md:text-4xl',
              titleFont,
            )}
          >
            TU PROYECTO
          </h2>
        </div>

        <div className="flex flex-col items-center gap-4 text-center">
          <div
            className={cn(
              'inline-block px-6 py-3 font-bold text-2xl sm:text-3xl',
              titleFont,
              config.pillClass,
            )}
          >
            {config.label}
          </div>

          {status !== 'unsubmitted' && project.lastSubmissionAt && (
            <div className="space-y-1">
              {project.lastSubmissionCommitSha && (
                <p className="font-mono text-primary text-sm">
                  <Link
                    href={`/hacker/submissions/${project.lastSubmissionCommitSha.substring(0, 7)}`}
                    className="font-bold underline"
                  >
                    {project.lastSubmissionCommitSha.substring(0, 7)}
                  </Link>
                  {project.lastSubmissionCommitTitle && (
                    <span> · {project.lastSubmissionCommitTitle}</span>
                  )}
                </p>
              )}
              {(project.lastSubmissionCommitAuthor ||
                project.lastSubmissionCommittedAt) && (
                <p className={cn('text-primary/70 text-sm', titleFont)}>
                  Commit
                  {project.lastSubmissionCommitAuthor && (
                    <>
                      {' '}por{' '}
                      <span className="font-bold text-primary">
                        {project.lastSubmissionCommitAuthor}
                      </span>
                    </>
                  )}
                  {project.lastSubmissionCommittedAt && (
                    <>
                      {' '}
                      <span className="font-bold text-primary">
                        {formatDistanceToNow(
                          new Date(project.lastSubmissionCommittedAt),
                          { addSuffix: true },
                        )}
                      </span>
                    </>
                  )}
                </p>
              )}
              <p className={cn('text-primary/70 text-sm', titleFont)}>
                Última validación:{' '}
                <span className="font-bold text-primary">
                  {formatDistanceToNow(new Date(project.lastSubmissionAt), {
                    addSuffix: true,
                  })}
                </span>
              </p>
            </div>
          )}

          {status === 'unsubmitted' && (
            <p className={cn('max-w-md text-primary/70 text-sm', titleFont)}>
              {submissionHint}
            </p>
          )}
        </div>

        {status === 'requirement_missing' && (
          <div className="space-y-3">
            {parseErrors(project.submissionError).map((line) => (
              <div
                key={line}
                className="border-destructive border-l-4 pl-4 font-mono text-primary text-sm"
              >
                {line}
              </div>
            ))}
            <p className={cn('pt-2 text-primary/70 text-sm', titleFont)}>
              Mira tu canal en Discord para más detalle.
            </p>
          </div>
        )}

        <div className="flex justify-center pt-2">
          <Link
            href="/hacker/submissions"
            className={cn(
              'border-2 border-primary px-4 py-2 font-bold text-primary text-sm transition-colors hover:bg-primary hover:text-background',
              titleFont,
            )}
          >
            VER TODAS LAS ENTREGAS
          </Link>
        </div>
      </div>
    </div>
  );
}
